import { stText, useStandaloneLanguage } from "../../../i18n/streamingPagesPresentation";
/**
 * LibraryPickerPopover -- Hierarchical library + sub-folder picker in a floating popover.
 *
 * Replaces both the broken TagPicker (EnabledFolderNames) and standalone
 * LibraryAccessPicker with a single unified control. Libraries are shown
 * as expandable tree rows with sub-folder exclusion checkboxes (Emby only).
 *
 * Closed state: pill badges for enabled libraries (or "All Libraries" pill).
 * Open state: search + tree list with checkboxes, expand/collapse, status dots.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { Badge } from "../../ui/Badge";
import { Icon } from "../../ui/Icon";
import { Tooltip } from "../../ui/Tooltip";
import { useAnchoredPosition } from "../../ui/useAnchoredPosition";
import { cn } from "../../../utils/cn";

// ============================================================================
// Types
// ============================================================================

export type LibraryPickerPopoverProps = {
  libraries: Array<{
    id: string;
    name: string;
    subFolders: Array<{ compositeId: string; path: string }>;
  }>;
  enabledFolderIds: string[];
  excludedSubFolderIds: string[];
  allFoldersEnabled: boolean;
  serverType: "emby" | "jellyfin" | string;
  disabled?: boolean;
  onLibraryChange: (libraryIds: string[]) => void;
  onSubFolderChange: (excludedIds: string[]) => void;
};

// ============================================================================
// Helpers
// ============================================================================

/** Normalize an ID for comparison (strip dashes, lowercase) */
export function normalizeId(id: string): string {
  return id.replace(/-/g, "").toLowerCase();
}

/** Extract the last path segment as a readable folder name */
function pathBasename(fullPath: string): string {
  const parts = fullPath.replace(/\/+$/, "").split("/");
  return parts[parts.length - 1] || fullPath;
}

/** Simple fuzzy match: all query chars appear in text in order */
function fuzzyMatch(text: string, query: string): boolean {
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  let qi = 0;
  for (let i = 0; i < lower.length && qi < q.length; i++) {
    if (lower[i] === q[qi]) qi++;
  }
  return qi === q.length;
}

// ============================================================================
// Component
// ============================================================================

export const LibraryPickerPopover = ({
  libraries,
  enabledFolderIds,
  excludedSubFolderIds,
  allFoldersEnabled,
  serverType,
  disabled = false,
  onLibraryChange,
  onSubFolderChange,
}: LibraryPickerPopoverProps) => {
  useStandaloneLanguage();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [expandedLibs, setExpandedLibs] = useState<Set<string>>(new Set());

  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const anchored = useAnchoredPosition({ open, anchor: triggerRef, panelRef });

  const showSubFolders = serverType === "emby";

  // Build normalized sets for fast lookup
  const enabledSet = useMemo(
    () => new Set(enabledFolderIds.map(normalizeId)),
    [enabledFolderIds],
  );
  const excludedSet = useMemo(
    () => new Set(excludedSubFolderIds),
    [excludedSubFolderIds],
  );

  // Compute summary stats for the trigger area
  const enabledCount = useMemo(() => {
    if (allFoldersEnabled) return libraries.length;
    return libraries.filter((lib) => enabledSet.has(normalizeId(lib.id))).length;
  }, [allFoldersEnabled, libraries, enabledSet]);

  const totalExcluded = excludedSubFolderIds.length;

  // Filter libraries (and their sub-folders) by search query
  const filteredLibraries = useMemo(() => {
    if (!query.trim()) return libraries;
    const q = query.trim();
    return libraries.filter((lib) => {
      // Match library name
      if (fuzzyMatch(lib.name, q)) return true;
      // Match any sub-folder basename
      if (showSubFolders && lib.subFolders.some((sf) => fuzzyMatch(pathBasename(sf.path), q))) return true;
      return false;
    });
  }, [libraries, query, showSubFolders]);

  // Reset query when opening; focus input
  useEffect(() => {
    if (open) {
      setQuery("");
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  // Library toggle
  const handleLibraryToggle = useCallback(
    (libId: string) => {
      const normId = normalizeId(libId);
      const isEnabled = enabledSet.has(normId);
      let next: string[];
      if (isEnabled) {
        next = enabledFolderIds.filter((id) => normalizeId(id) !== normId);
      } else {
        next = [...enabledFolderIds, libId];
      }
      onLibraryChange(next);
    },
    [enabledFolderIds, enabledSet, onLibraryChange],
  );

  // Sub-folder exclusion toggle (inverted: checked = included, unchecked = excluded)
  const handleSubFolderToggle = useCallback(
    (compositeId: string) => {
      const isExcluded = excludedSet.has(compositeId);
      let next: string[];
      if (isExcluded) {
        next = excludedSubFolderIds.filter((id) => id !== compositeId);
      } else {
        next = [...excludedSubFolderIds, compositeId];
      }
      onSubFolderChange(next);
    },
    [excludedSubFolderIds, excludedSet, onSubFolderChange],
  );

  const toggleExpand = useCallback((libId: string) => {
    setExpandedLibs((prev) => {
      const next = new Set(prev);
      if (next.has(libId)) next.delete(libId);
      else next.add(libId);
      return next;
    });
  }, []);

  // ---- Trigger (closed state) ----
  const renderTrigger = () => {
    // Build pill list
    const pills: Array<{ key: string; label: string; variant: "primary" | "warning" }> = [];

    if (allFoldersEnabled) {
      pills.push({ key: "__all__", label: stText("allLibraries"), variant: "primary" });
    } else {
      for (const lib of libraries) {
        if (enabledSet.has(normalizeId(lib.id))) {
          pills.push({ key: lib.id, label: lib.name, variant: "primary" });
        }
      }
    }

    return (
      <div
        ref={triggerRef}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={stText("libraryAccess2")}
        tabIndex={disabled ? -1 : 0}
        onClick={() => { if (!disabled) setOpen(true); }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={cn(
          "flex flex-wrap items-center gap-1 rounded-md border border-border/50 bg-background px-2 py-1.5 min-h-[32px] cursor-pointer",
          "hover:border-border transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:border-primary/50",
          disabled && "opacity-50 cursor-not-allowed",
          open && "ring-2 ring-primary/40 border-primary/50",
        )}
      >
        {pills.length === 0 && (
          <span className="text-xs text-muted-foreground">{stText("noLibrariesSelected")}</span>
        )}
        {pills.map((pill) => (
          <span
            key={pill.key}
            className="inline-flex items-center gap-0.5 bg-primary/10 text-primary-text border border-primary/20 rounded px-1.5 py-0.5 text-[10px] 2xl:text-xs"
          >
            {pill.label}
          </span>
        ))}
        {totalExcluded > 0 && (
          <span className="inline-flex items-center gap-0.5 bg-amber-500/15 text-amber-600 border border-amber-500/30 rounded px-1.5 py-0.5 text-[10px] 2xl:text-xs">{stText("inline53", { value0: totalExcluded })}</span>
        )}
        <Icon
          icon="lucide:chevron-down"
          className={cn(
            "h-3 w-3 text-muted-foreground/40 ml-auto shrink-0 transition-transform",
            open && "rotate-180",
          )}
        />
      </div>
    );
  };

  // ---- Panel (open state) ----
  const renderPanel = () => (
    <div
      ref={panelRef}
      role="listbox"
      aria-label={stText("libraryPicker")}
      style={{
        ...anchored.style,
        width: triggerRef.current?.offsetWidth ?? 320,
        maxHeight: "min(60vh, 400px, var(--qb-available-h, 100vh))",
      }}
      className={cn(
        "z-[9999] flex flex-col overflow-hidden rounded-lg border border-border/60 bg-card/95 text-sm text-foreground shadow-soft outline-none",
        anchored.className,
      )}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Search input */}
      <div className="flex items-center gap-2 px-2.5 py-2 border-b border-border/30">
        <Icon icon="lucide:search" className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={stText("searchLibraries")}
          className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground border-none outline-none p-0"
          aria-label={stText("searchLibraries2")}
          autoComplete="off"
          spellCheck={false}
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              inputRef.current?.focus();
            }}
            className="text-muted-foreground/80 hover:text-muted-foreground transition-colors"
            tabIndex={-1}
            aria-label={stText("clearSearch")}
          >
            <Icon icon="lucide:x" className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Summary bar */}
      <div className="flex items-center gap-2 px-2.5 py-1 border-b border-border/20 text-[10px] text-muted-foreground">
        <span>{stText("inline54", { value0: enabledCount, value1: libraries.length })}</span>
        {allFoldersEnabled && (
          <Badge variant="info" size="xs">{stText("allAccess")}</Badge>
        )}
        {totalExcluded > 0 && (
          <Badge variant="warning" size="xs">{stText("inline55", { value0: totalExcluded })}</Badge>
        )}
      </div>

      {/* Tree list */}
      <div className="overflow-y-auto qb-scroll flex-1">
        {filteredLibraries.length === 0 && (
          <div className="px-3 py-4 text-center text-xs text-muted-foreground">
            {query ? stText("noMatchingLibraries") : stText("noLibrariesAvailable")}
          </div>
        )}

        {filteredLibraries.map((lib) => {
          const isEnabled = allFoldersEnabled || enabledSet.has(normalizeId(lib.id));
          const isExpanded = expandedLibs.has(lib.id);
          const hasSubFolders = showSubFolders && lib.subFolders.length > 1;
          const excludedCount = hasSubFolders
            ? lib.subFolders.filter((sf) => sf.compositeId && excludedSet.has(sf.compositeId)).length
            : 0;

          // When searching, auto-expand libraries whose sub-folders match
          const shouldAutoExpand = Boolean(query.trim()) && hasSubFolders &&
            lib.subFolders.some((sf) => fuzzyMatch(pathBasename(sf.path), query.trim()));
          const showExpanded = isExpanded || shouldAutoExpand;

          return (
            <div key={lib.id}>
              {/* Library row */}
              <div
                className={cn(
                  "flex items-center gap-1.5 py-1 px-2 transition-colors",
                  isEnabled ? "hover:bg-muted/30" : "hover:bg-muted/15",
                )}
              >
                {/* Expand/collapse chevron */}
                {hasSubFolders ? (
                  <button
                    type="button"
                    onClick={() => toggleExpand(lib.id)}
                    className="flex items-center p-0.5 text-muted-foreground/80 hover:text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 rounded-sm"
                    aria-expanded={showExpanded}
                    aria-label={stText(showExpanded ? "collapseLibrary" : "expandLibrary", { name: lib.name })}
                  >
                    <Icon
                      icon="lucide:chevron-right"
                      className={cn("h-3 w-3 transition-transform", showExpanded && "rotate-90")}
                    />
                  </button>
                ) : (
                  <span className="w-4 shrink-0" />
                )}

                {/* Checkbox */}
                <label className="flex items-center shrink-0 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={() => handleLibraryToggle(lib.id)}
                    disabled={disabled || allFoldersEnabled}
                    aria-label={stText("nameLibraryAccess", { name: lib.name })}
                    className="h-3.5 w-3.5 rounded border-border bg-background text-primary accent-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </label>

                {/* Status dot */}
                <span
                  className={cn(
                    "h-1.5 w-1.5 rounded-full shrink-0 transition-colors",
                    isEnabled ? "bg-emerald-500" : "bg-muted-foreground/25",
                  )}
                  aria-hidden="true"
                />

                {/* Library name */}
                <span
                  className={cn(
                    "text-xs 2xl:text-sm flex-1 truncate transition-colors",
                    isEnabled ? "text-foreground font-medium" : "text-muted-foreground",
                  )}
                >
                  {lib.name}
                </span>

                {/* Sub-folder count + excluded badge */}
                {hasSubFolders && (
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {lib.subFolders.length}
                  </span>
                )}
                {excludedCount > 0 && (
                  <Badge variant="warning" size="xs" className="shrink-0">
                    {excludedCount}
                  </Badge>
                )}
              </div>

              {/* Sub-folder rows (Emby only, expanded) */}
              {hasSubFolders && showExpanded && (
                <div className="ml-5 border-l border-border/30 pl-2">
                  {lib.subFolders.map((sf) => {
                    if (!sf.compositeId) return null;
                    const isExcluded = excludedSet.has(sf.compositeId);
                    const isSubEnabled = isEnabled && !isExcluded;
                    const basename = pathBasename(sf.path);

                    // Dim sub-folders that don't match search (but still show them)
                    const matchesQuery = !query.trim() || fuzzyMatch(basename, query.trim());

                    return (
                      <div
                        key={sf.compositeId}
                        className={cn(
                          "flex min-w-0 items-center gap-1.5 py-0.5 px-1.5 rounded-md transition-colors",
                          isSubEnabled ? "hover:bg-muted/20" : "hover:bg-muted/10",
                          !matchesQuery && "opacity-40",
                        )}
                      >
                        <label className="flex items-center shrink-0 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!isExcluded}
                            onChange={() => handleSubFolderToggle(sf.compositeId)}
                            disabled={disabled || !isEnabled}
                            aria-label={stText("includeBasenameSubFolder", { basename: basename })}
                            className="h-3 w-3 rounded border-border bg-background text-primary accent-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                          />
                        </label>

                        <span
                          className={cn(
                            "h-1 w-1 rounded-full shrink-0 transition-colors",
                            isSubEnabled ? "bg-emerald-400" : "bg-muted-foreground/20",
                          )}
                          aria-hidden="true"
                        />

                        <Tooltip content={sf.path} className="block min-w-0">
                          <span
                            className={cn(
                              "block min-w-0 truncate text-[11px] 2xl:text-xs transition-colors",
                              isSubEnabled ? "text-foreground" : "text-muted-foreground/60 line-through",
                            )}
                          >
                            {basename}
                          </span>
                        </Tooltip>

                        {isExcluded && (
                          <Badge variant="muted" size="xs" className="shrink-0 opacity-70">
                            {stText("excluded")}</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer hint */}
      <div className="px-2.5 py-1.5 border-t border-border/20 text-[10px] text-muted-foreground">
        {showSubFolders
          ? stText("expandLibrariesToManageSubFolderExclusions")
          : stText("toggleLibrariesToControlAccess")}
      </div>
    </div>
  );

  return (
    <>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-xs 2xl:text-sm text-foreground">{stText("libraryAccess")}</span>
        <span className="text-[10px] 2xl:text-xs text-muted-foreground ml-auto">
          {stText("libraryAccessCounts", { enabled: enabledCount, excluded: totalExcluded })}
        </span>
      </div>
      {renderTrigger()}
      {open && createPortal(renderPanel(), document.body)}
    </>
  );
};
